export const defaultPasswordPolicyComplexity: any = {
  minLowerCase: 1,
  minUpperCase: 1,
  minNumber: 1,
  minSymbol: 1,
  minLength: 9,
}

/**
 * Convert number into words
 * @param {number} value
 */
const getNumbersWord = (value: number) => {
  const numbersWord: string[] = ['One']
  const numberWord: string = numbersWord[value - 1]
  return numberWord ?? value
}

/**
 * Based on password policy received from Okta generate the password requirement
 * complexity
 * @param {*} passwordPolicyComplexity - This policy will get from Okta
 */
export const getPasswordRequirementComplexity = (
  passwordPolicyComplexity: any,
  newPassword: string,
) => {
  const updatedPasswordPolicyComplexity: any = {
    ...defaultPasswordPolicyComplexity,
    ...passwordPolicyComplexity,
  }

  const formattedPasswordComplexityRequirement = {
    minLowerCaseStr: `${getNumbersWord(
      updatedPasswordPolicyComplexity.minLowerCase,
    )} lowercase character`,
    minUpperCaseStr: `${getNumbersWord(
      updatedPasswordPolicyComplexity.minUpperCase,
    )} uppercase character`,
    minNumberStr: `${getNumbersWord(
      updatedPasswordPolicyComplexity.minNumber,
    )} number`,
    minSymbolStr: `${getNumbersWord(
      updatedPasswordPolicyComplexity.minSymbol,
    )} special character`,
    minLengthStr: `${updatedPasswordPolicyComplexity.minLength} characters minimum`,
  }

  const {
    isPasswordComplex,
    hasMinLength,
    hasLowerCase,
    hasUpperCase,
    hasDigit,
    hasSymbol,
  } = getPasswordComplexity(
    newPassword,
    updatedPasswordPolicyComplexity.minLength,
  )

  return {
    isPasswordComplex,
    passwordComplexityInfo: [
      {
        isActiveClass: hasLowerCase,
        item: formattedPasswordComplexityRequirement.minLowerCaseStr,
      },
      {
        isActiveClass: hasSymbol,
        item: formattedPasswordComplexityRequirement.minSymbolStr,
      },
      {
        isActiveClass: hasUpperCase,
        item: formattedPasswordComplexityRequirement.minUpperCaseStr,
      },
      {
        isActiveClass: hasMinLength,
        item: formattedPasswordComplexityRequirement.minLengthStr,
      },
      {
        isActiveClass: hasDigit,
        item: formattedPasswordComplexityRequirement.minNumberStr,
      },
    ],
  }
}

/**
 * Returns password complexity object for Reset Password module
 * @param password
 * @param minLength - min length of password
 */
export const getPasswordComplexity = (
  password: string,
  minLength: number,
): any => {
  const hasMinLength = password?.length >= minLength
  const hasLowerCase = password?.length > 0 && /[a-z]/g.test(password)
  const hasUpperCase = /[A-Z]/g.test(password)
  const hasDigit = /[0-9]/g.test(password)
  const hasSymbol = /\W/g.test(password)

  return {
    isPasswordComplex:
      hasLowerCase && hasUpperCase && hasDigit && hasSymbol && hasMinLength,
    hasMinLength,
    hasLowerCase,
    hasUpperCase,
    hasDigit,
    hasSymbol,
  }
}
